import { GlobalIcon } from "get-life-storybook-ts";
import { useRef, useState } from "react";
import {
  CapitalsResponse,
  NewCheckoutI,
  PurchasePeriodT,
} from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import numberFormat, {
  numberFormatPayment,
} from "../../../../../../utils/Utils";
import {
  formatGetlifeDate,
} from "../../../../../../utils/PrepareDataCalendar";
import { GlobalDomainQuestions } from "../../../../../../utils/InternationlChanges";
import { DOMAIN } from "../../../../../../constants/Global";
import {
  CouponInfoI,
  NativeCouponStateI,
  ValidateCouponT,
} from "../../PaymentPage.controller";
import BenefitsModal from "../../../../../../components/BenefitsModal/BenefitsModal";
import NativeCoupon from "./NativeCoupon.component";
import { ConditionsPromoLink } from "../../../Coupon/Coupon.component";
import { CAPITAL_TRAITS_FOR_TOTAL, PricesV3T, PromotionV3T } from "../types";
import FreqCards from "./FreqCards.component";
import { chargedPriceForSelectedPeriod } from "../../../../../../utils/pricesV3";

interface OrderSummaryV3Props {
  checkout: NewCheckoutI;
  capitals?: CapitalsResponse;
  prices?: PricesV3T;
  promotion?: PromotionV3T;
  isAvailableToggle: boolean;
  translate: (key: string) => string;
  validateCoupon?: ValidateCouponT;
  couponInfo?: CouponInfoI;
  handlePurchasePeriod: (value: PurchasePeriodT) => void;
  handleAddCoupon: (code: string, yearlyPrice: number) => void;
  handleRemoveCoupon: (code: string) => void;
  onConditionsClick: () => void;
  /** Native: cupón del lead en ms-agl (sustituye a la casilla del monolito). */
  nativeCoupon?: NativeCouponStateI;
  /**
   * When this summary is embedded inside the mobile collapsed card, the
   * freq cards already live above it — skip rendering them again here.
   */
  hideFreqCards?: boolean;
}

const traitToCamelCase = (trait: string): string =>
  trait
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+(.)?/g, (_: string, c: string) =>
      c ? c.toUpperCase() : ""
    );

const OrderSummaryV3 = ({
  checkout,
  capitals,
  prices,
  promotion,
  isAvailableToggle,
  translate,
  validateCoupon,
  couponInfo,
  handlePurchasePeriod,
  handleAddCoupon,
  handleRemoveCoupon,
  onConditionsClick,
  nativeCoupon,
  hideFreqCards,
}: OrderSummaryV3Props) => {
  const benefitsModalRef = useRef<any>(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState(checkout.promotionalCode || "");

  const period = (checkout.period?.toLowerCase() || "monthly") as
    | "monthly"
    | "yearly";
  const isYearly = period === "yearly";

  // Multi-capital handling (mirrors ResumePrice).
  // The breakdown shows every active trait, but the chip "totalCapital"
  // sums only the traits in CAPITAL_TRAITS_FOR_TOTAL (DEATH + SERIOUS_ILLNESS),
  // since DISABILITY overlaps with DEATH and would double-count.
  const activeTraits = capitals
    ? (Object.keys(capitals) as Array<keyof CapitalsResponse>).filter(
        (key) => capitals[key]?.active
      )
    : [];
  const hasMultiCapital = activeTraits.length > 0;
  const totalCapital = hasMultiCapital
    ? activeTraits
        .filter((key) =>
          (CAPITAL_TRAITS_FOR_TOTAL as readonly string[]).includes(key)
        )
        .reduce((sum, key) => sum + (capitals![key]?.capital ?? 0), 0)
    : checkout.capital;

  // Prefer values from `api/pricing/capitals` (gives us BOTH frequencies up
  // front so the toggle can show real prices without re-fetching). Fall back
  // to checkout fields for markets where this endpoint isn't called.
  const periodPrices = prices?.[period];

  const totalFull = periodPrices?.real ?? checkout.regularPrice;
  // The checkout is re-fetched for the selected period: its priceToPay is the
  // amount actually charged, so it wins over `prices` (both frequencies).
  const totalNow =
    chargedPriceForSelectedPeriod(checkout) ??
    periodPrices?.promoted ??
    (checkout.isPromoted ? checkout.priceToPay : checkout.regularPrice);
  const isPromoted = totalNow < totalFull;
  const saved = Math.max(0, totalFull - totalNow);

  const handleApplyCoupon = () => {
    const code = couponInput.trim();
    if (!code) return;
    handleAddCoupon(code, checkout.finalYearlyPrice);
  };

  const removeCoupon = () => {
    if (couponInfo?.code) handleRemoveCoupon(couponInfo.code);
    else if (checkout.promotionalCode) handleRemoveCoupon(checkout.promotionalCode);
    setCouponInput("");
  };

  const couponApplied = validateCoupon === "validate" && !!couponInfo?.code;
  const couponInvalid = validateCoupon === "invalidate";

  return (
    <aside className="paymentV3Summary">
      {/* Header — capital now lives in the page title, not here. */}
      <div className="paymentV3Summary__header">
        <span className="paymentV3Summary__eyebrow">
          {translate("paymentV3.summary.eyebrow")}
        </span>
      </div>

      {/* Frequency cards (hidden when this summary is nested inside the
          mobile collapsed card, which renders its own copy above). */}
      {isAvailableToggle && !hideFreqCards && (
        <FreqCards
          checkout={checkout}
          prices={prices}
          promotion={promotion}
          translate={translate}
          handlePurchasePeriod={handlePurchasePeriod}
        />
      )}

      {/* Coverage breakdown */}
      {DOMAIN !== "wl_es" && (
        <div className="paymentV3Summary__breakdown">
          {hasMultiCapital ? (
            activeTraits.map((trait) => (
              <div className="paymentV3Summary__breakdownRow" key={trait}>
                <span>
                  {translate(`payment.multiproduct.${traitToCamelCase(trait)}`)}
                </span>
                <strong>
                  {numberFormat(capitals![trait]?.capital ?? 0, "capital")}
                </strong>
              </div>
            ))
          ) : (
            <div className="paymentV3Summary__breakdownRow">
              <span>{translate("paymentV3.summary.breakdown.coverage")}</span>
              <strong>{numberFormatPayment(totalCapital, "capital")} €</strong>
            </div>
          )}
          <button
            type="button"
            className="paymentV3Summary__benefitsLink"
            data-track="coverage_benefits"
            onClick={() => benefitsModalRef.current?.handleShowModal()}
          >
            {translate("paymentV3.summary.breakdown.benefitsLink")}
            <GlobalIcon iconName="ArrowRightIcon" size="XS" color="#423EF4" />
          </button>
        </div>
      )}

      <hr className="paymentV3Summary__divider" />

      {/* Total — shown once. The discount is communicated in the frequency
          selector and in the coupon line below, so no strikethrough here. */}
      <div className="paymentV3Summary__total">
        <span className="paymentV3Summary__totalLabel">
          {translate("paymentV3.summary.total")}
        </span>
        <div className="paymentV3Summary__totalRight">
          <span className="paymentV3Summary__totalNow">
            {numberFormatPayment(totalNow)} €
          </span>
          <span className="paymentV3Summary__totalSub">
            {translate(
              isYearly
                ? "paymentV3.summary.freq.perYear"
                : "paymentV3.summary.freq.perMonth"
            )}{" "}
            · {translate("paymentV3.summary.taxes")}
          </span>
        </div>
      </div>

      {/* Mini-timeline */}
      {checkout.feeCollection && checkout.feeCollection.length > 0 && (
        <div className="paymentV3Summary__timeline">
          <div className="paymentV3Summary__timelineTitle">
            {translate("paymentV3.summary.timeline.title")}
          </div>
          <div className="paymentV3Summary__timelineDots">
            <span className="paymentV3Summary__timelineConnector" />
            <div className="paymentV3Summary__timelineDot paymentV3Summary__timelineDot--highlight">
              <span className="paymentV3Summary__timelineBullet" />
              <span className="paymentV3Summary__timelineDate">
                {translate("paymentV3.summary.timeline.todayLabel")}
              </span>
              <span className="paymentV3Summary__timelineLabel">
                {translate("paymentV3.summary.timeline.verification")}
              </span>
              <span className="paymentV3Summary__timelineSub">0,50 €</span>
            </div>
            <div className="paymentV3Summary__timelineDot">
              <span className="paymentV3Summary__timelineBullet" />
              <span className="paymentV3Summary__timelineDate">
                {formatGetlifeDate(
                  checkout.feeCollection[0].expectedPaymentDate,
                  GlobalDomainQuestions[DOMAIN as keyof typeof GlobalDomainQuestions]
                )}
              </span>
              <span className="paymentV3Summary__timelineLabel">
                {translate("paymentV3.summary.timeline.firstFee")}
              </span>
              <span className="paymentV3Summary__timelineSub">
                {translate("paymentV3.summary.timeline.prorated")}
              </span>
            </div>
            {checkout.feeCollection[1] && (
              <div className="paymentV3Summary__timelineDot">
                <span className="paymentV3Summary__timelineBullet" />
                <span className="paymentV3Summary__timelineDate">
                  {formatGetlifeDate(
                    checkout.feeCollection[1].expectedPaymentDate,
                    GlobalDomainQuestions[DOMAIN as keyof typeof GlobalDomainQuestions]
                  )}
                </span>
                <span className="paymentV3Summary__timelineLabel">
                  {translate(
                    isYearly
                      ? "paymentV3.summary.timeline.renewal"
                      : "paymentV3.summary.timeline.fullMonth"
                  )}
                </span>
                <span className="paymentV3Summary__timelineSub">
                  {translate(
                    isYearly
                      ? "paymentV3.summary.timeline.firm"
                      : "paymentV3.summary.timeline.fullMonthSub"
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coupon — applied chip OR collapsible button + input.
          The applied chip carries the coupon savings in a single line, so the
          separate discount line + green savings box are no longer needed.
          Native: the lead's coupon lives in ms-agl (NativeCoupon). */}
      {nativeCoupon ? (
        <NativeCoupon {...nativeCoupon} translate={translate} />
      ) : couponApplied ? (
        <div className="paymentV3Summary__couponApplied">
          <span className="paymentV3Summary__couponAppliedLeft">
            <span className="paymentV3Summary__couponAppliedBadge">%</span>
            <span>
              <span className="paymentV3Summary__couponAppliedCode">
                {couponInfo!.code}
              </span>
              <span className="paymentV3Summary__couponAppliedSub">
                {isPromoted && saved > 0
                  ? translate("paymentV3.summary.coupon.savings")
                      .replace("{amount}", `${numberFormatPayment(saved)} €`)
                      .replace(
                        "{period}",
                        translate(
                          isYearly
                            ? "paymentV3.summary.freq.perYearLowercase"
                            : "paymentV3.summary.freq.perMonthLowercase"
                        )
                      )
                  : translate("paymentV3.summary.coupon.applied")}
              </span>
            </span>
          </span>
          {checkout.promoRemovable !== false && (
            <button
              type="button"
              className="paymentV3Summary__couponRemove"
              onClick={removeCoupon}
              aria-label={translate("paymentV3.summary.coupon.removeAria")}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="paymentV3Summary__couponToggle"
            onClick={() => setCouponOpen((o) => !o)}
          >
            <span>
              <span className="paymentV3Summary__couponToggleBadge">%</span>
              {translate("paymentV3.summary.coupon.cta")}
            </span>
            <span className="paymentV3Summary__couponToggleIcon">
              {couponOpen ? "−" : "+"}
            </span>
          </button>
          {couponOpen && (
            <>
              <div className="paymentV3Summary__couponInputRow">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder={translate("paymentV3.summary.coupon.placeholder")}
                  className="paymentV3Summary__couponInput"
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  className="paymentV3Summary__couponApply"
                  disabled={!couponInput.trim()}
                >
                  {translate("paymentV3.summary.coupon.apply")}
                </button>
              </div>
              {couponInvalid && (
                <div className="paymentV3Summary__couponError">
                  {translate("coupons.inputText.invalid")}
                </div>
              )}
            </>
          )}
        </>
      )}

      {couponInfo && (
        <div className="paymentV3Summary__couponConditions">
          <ConditionsPromoLink
            translate={translate}
            onConditionsClick={onConditionsClick}
          />
        </div>
      )}

      <BenefitsModal
        ref={benefitsModalRef}
        title={translate("calculatorPage.benefitsModal.title")}
        benefits={[
          {
            icon: (
              <GlobalIcon
                iconName="CalendarBrandIcon"
                size="M"
                color="var(--theme-primary)"
                filled="var(--theme-fill)"
              />
            ),
            title: translate("calculatorPage.benefitsModal.benefit1.title"),
            description: translate(
              "calculatorPage.benefitsModal.benefit1.description"
            ),
          },
          {
            icon: (
              <GlobalIcon
                iconName="TransparencyIcon"
                size="M"
                color="var(--theme-primary)"
                filled="var(--theme-fill)"
              />
            ),
            title: translate("calculatorPage.benefitsModal.benefit2.title"),
            description: translate(
              "calculatorPage.benefitsModal.benefit2.description"
            ),
          },
          {
            icon: (
              <GlobalIcon
                iconName="SaveMoneyIcon"
                size="M"
                color="var(--theme-primary)"
                filled="var(--theme-fill)"
              />
            ),
            title: translate("calculatorPage.benefitsModal.benefit3.title"),
            description: translate(
              "calculatorPage.benefitsModal.benefit3.description"
            ),
          },
        ]}
      />
    </aside>
  );
};

export default OrderSummaryV3;
